import React, { useState, useEffect } from "react";
import { Box, Container, Heading, Text, SimpleGrid, Icon, VStack, Button, Image, Flex, Grid, useColorModeValue, UnorderedList, ListItem, HStack, Badge, Stat, StatLabel, StatNumber, StatHelpText } from "@chakra-ui/react";
import { FaCode, FaMobileAlt, FaServer, FaRobot, FaWhatsapp, FaStar, FaUsers, FaClock, FaShieldAlt, FaBrain, FaLaptopCode, FaInternetExplorer, FaAndroid, FaApple, FaSwift, FaReact, FaPython, FaPhp, FaAngular, FaNodeJs, FaVuejs, FaLaravel, FaCheckCircle, FaDatabase } from 'react-icons/fa';
import { TbMessageChatbot } from "react-icons/tb";
import { AiOutlineSolution, AiTwotoneApi } from "react-icons/ai";
import { MdSystemSecurityUpdateGood } from "react-icons/md";
import { DiDotnet, DiCodeigniter, DiAws } from "react-icons/di";
import { IoIosRocket } from "react-icons/io";
import { SiGraphql, SiZend, SiMongodb, SiExpress, SiNodedotjs, SiWordpress, SiReact as SiReactIcon, SiBitcoin } from "react-icons/si";
import NavbarTopFirst from "../Navbar/NavbarTopFirstt";
import NavbarTopSecond from "../Navbar/NavbarTopSecond";
import { useMediaQuery } from 'react-responsive';
import { Helmet } from "react-helmet";
import { motion, useScroll, useTransform } from "framer-motion";
import HeaderFirst from "../Header/HeaderFirst";
import CardFirst from "../HeaderCard/CardFirst";
import Contact from "../pages/Contact";
import Appointment from "../pages/Appointment";
import { Link, useLocation } from "react-router-dom";
import { initializeNavbarState, handleTechnologyClick, resetNavbarState } from "../../utils/navbarUtils";
import technologyDetails from "../../data/technologyDetails";

const MotionBox = motion(Box);

// Color variables from environment
const primaryColor = process.env.REACT_APP_PRIMARY_COLOR || "#3182CE";
const secondaryColor = process.env.REACT_APP_SECONDARY_COLOR || "#319795";
const backgroundColor = process.env.REACT_APP_BACKGROUND_COLOR || "#F7FAFC";
const textColor = process.env.REACT_APP_TEXT_COLOR || "#2D3748";
const cardBackground = process.env.REACT_APP_CARD_BACKGROUND || "#FFFFFF";
const modalBackground = process.env.REACT_APP_MODAL_BACKGROUND || "#FFFFFF";
const buttonHoverColor = process.env.REACT_APP_BUTTON_HOVER_COLOR || "#EDF2F7";
const gradientStart = process.env.REACT_APP_GRADIENT_START || "blue.500";
const gradientEnd = process.env.REACT_APP_GRADIENT_END || "teal.500";

function ServiceCard({ title, description, icon, features, color }) {
  return (
    <MotionBox
      p={8}
      bg={cardBackground}
      borderRadius="xl"
      boxShadow="lg"
      borderWidth="1px"
      borderColor="gray.200"
      transition="all 0.3s"
      whileHover={{ y: -10 }}
      _hover={{
        boxShadow: "2xl",
        borderColor: primaryColor
      }}
    >
      <VStack spacing={6} align="start">
        <Box
          p={4}
          borderRadius="full"
          bg={`${primaryColor}10`}
          color={primaryColor}
        >
          <Icon as={icon} w={8} h={8} color={color} />
        </Box>
        <Heading size="md" color={textColor} fontWeight="bold">{title}</Heading>
        <Text color="gray.600" fontSize="md" lineHeight="1.6">{description}</Text>
        {features && (
          <VStack spacing={2} align="start" w="100%">
            {features.map((feature, index) => (
              <HStack key={index} spacing={2}>
                <Icon as={FaCheckCircle} color={primaryColor} />
                <Text color="gray.600" fontSize="sm">{feature}</Text>
              </HStack>
            ))}
          </VStack>
        )}
      </VStack>
    </MotionBox>
  );
}

function FeatureCard({ title, description, icon, color }) {
  return (
    <MotionBox
      p={8}
      bg={cardBackground}
      borderRadius="xl"
      boxShadow="lg"
      whileHover={{ y: -5 }}
      transition="all 0.3s"
      _hover={{
        boxShadow: "xl",
        transform: "translateY(-5px)"
      }}
    >
      <VStack spacing={4} align="start">
        <Box
          p={3}
          borderRadius="full"
          bg={`${color}10`}
          color={color}
        >
          <Icon as={icon} w={6} h={6} color={color} />
        </Box>
        <Heading size="md" color={textColor}>{title}</Heading>
        <Text color="gray.600" fontSize="sm">{description}</Text>
      </VStack>
    </MotionBox>
  );
}

function TestimonialCard({ name, role, content, image }) {
  return (
    <MotionBox
      p={8}
      bg={cardBackground}
      borderRadius="xl"
      boxShadow="lg"
      position="relative"
      whileHover={{ y: -5 }}
      transition="all 0.3s"
    >
      <Box position="absolute" top={4} left={4}>
        <Icon as={FaStar} color="yellow.400" w={5} h={5} />
      </Box>
      <VStack spacing={6} align="start">
        <HStack spacing={4}>
          <Image
            src={image}
            alt={name}
            borderRadius="full"
            boxSize="60px"
          />
          <Box>
            <Heading as="h4" size="md" color={textColor}>{name}</Heading>
            <Text fontSize="sm" color="gray.500">{role}</Text>
          </Box>
        </HStack>
        <Text color="gray.600" fontSize="md" lineHeight="1.8">
        "{content}"
      </Text>
        <HStack spacing={1}>
          {[...Array(5)].map((_, i) => (
            <Icon key={i} as={FaStar} color="yellow.400" />
          ))}
        </HStack>
      </VStack>
    </MotionBox>
  );
}

const TechnologyCard = ({ icon, name, onClick, color }) => {
  const buttonHoverColor = useColorModeValue("gray.100", "gray.700");
  return (
    <MotionBox
      onClick={onClick}
      cursor="pointer"
      p={4}
      borderRadius="lg"
      _hover={{ bg: buttonHoverColor }}
      transition="all 0.3s"
      whileHover={{ scale: 1.05 }}
    >
      <VStack spacing={3} alignItems="center" minW={{ base: "70px", md: "90px" }}>
        <Icon as={icon} w={10} h={10} color={color} />
        <Text fontSize="sm" textAlign="center" color={textColor}>{name}</Text>
      </VStack>
    </MotionBox>
  );
};

function Home() {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 704px)" });
  const [selectedTechnology, setSelectedTechnology] = useState(null);
  const [navbarTop, setNavbarTop] = useState(false);
  const [navbarTopTwo, setNavbarTopTwo] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showAllTechnologies, setShowAllTechnologies] = useState(false);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/') {
      resetNavbarState(setNavbarTop, setNavbarTopTwo, setSelectedTechnology);
    } else {
    initializeNavbarState(setNavbarTop, setNavbarTopTwo, setSelectedTechnology);
    }
  }, [location.pathname, setNavbarTop, setNavbarTopTwo, setSelectedTechnology]);

  // Add scroll to top when technology is selected
  useEffect(() => {
    if (selectedTechnology) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedTechnology]);

  const handleBackToHome = () => {
    resetNavbarState(setNavbarTop, setNavbarTopTwo, setSelectedTechnology);
    setShowContactForm(false);
    setShowAppointmentForm(false);
    setShowAllTechnologies(false);
    setSelectedTechnology(null);
  };

  const handleBookAppointmentClick = () => {
    setShowContactForm(true);
  };

  const handleGetStartedClick = () => {
    console.log("Get Started button clicked!");
    setShowAppointmentForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLearnMoreClick = () => {
    console.log("Learn More button clicked!");
    setShowAllTechnologies(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    {
      title: "Website Development",
      description: "Create stunning, responsive websites that drive business growth and deliver exceptional user experiences",
      icon: FaCode,
      features: ["Responsive Design", "SEO Optimization", "Performance Focus", "Modern Technologies"],
      color: "blue.500"
    },
    {
      title: "App Development",
      description: "Native and cross-platform mobile applications for iOS and Android with cutting-edge features",
      icon: FaMobileAlt,
      features: ["Native Performance", "Cross-Platform", "Offline Support", "Push Notifications"],
      color: "green.500"
    },
    {
      title: "API Development",
      description: "Robust and scalable APIs to power your applications with secure and efficient integration",
      icon: FaServer,
      features: ["RESTful APIs", "GraphQL", "Authentication", "Documentation"],
      color: "red.500"
    },
    {
      title: "Chatbot Development",
      description: "Intelligent chatbots that enhance customer engagement and automate business processes",
      icon: FaRobot,
      features: ["AI Integration", "Natural Language Processing", "Multi-platform", "Analytics"],
      color: "purple.500"
    },
    {
      title: "WhatsApp Bot Development",
      description: "Automate customer service and business operations with powerful WhatsApp Business API integration",
      icon: FaWhatsapp,
      features: ["Automated Responses", "CRM Integration", "Analytics", "Multi-language Support"],
      color: "teal.500"
    },
  ];

  const features = [
    {
      title: "Expert Team",
      description: "Our team consists of experienced developers and technology experts",
      icon: FaUsers,
      color: "orange.500"
    },
    {
      title: "Quality Assurance",
      description: "Rigorous testing and quality control for all our solutions",
      icon: FaShieldAlt,
      color: "blue.500"
    },
    {
      title: "24/7 Support",
      description: "Round-the-clock technical support and maintenance",
      icon: FaClock,
      color: "purple.500"
    },
  ];

  const testimonials = [
    {
      name: "John Smith",
      role: "CEO, TechCorp",
      content: "Working with this team has transformed our business. Their technical expertise and dedication to quality are unmatched.",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    },
    {
      name: "Sarah Johnson",
      role: "CTO, InnovateTech",
      content: "The solutions they've provided have significantly improved our operational efficiency. Highly recommended!",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    },
    {
      name: "Michael Brown",
      role: "Founder, StartupX",
      content: "Their team delivered beyond our expectations. The attention to detail and commitment to excellence is remarkable.",
      image: "https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    },
  ];

  const technologies = {
    web: [
      { name: ".Net", icon: DiDotnet, color: "#805AD5" },
      { name: "Python", icon: FaPython, color: "#3182CE" },
      { name: "Ruby On Rails", icon: FaCode, color: "#E53E3E" },
      { name: "GraphQL", icon: SiGraphql, color: "#ED64A6" },
      { name: "PHP", icon: FaPhp, color: "#805AD5" }
    ],
    php: [
      { name: "Laravel", icon: FaLaravel, color: "#C53030" },
      { name: "CodeIgniter", icon: DiCodeigniter, color: "#DD6B20" },
      { name: "Zend", icon: SiZend, color: "#3182CE" }
    ],
    mobile: [
      { name: "React Native", icon: FaReact, color: "#00B5D8" },
      { name: "Flutter", icon: FaMobileAlt, color: "#319795" },
      { name: "iOS", icon: FaApple, color: "#1A202C" },
      { name: "Android", icon: FaAndroid, color: "#38A169" }
    ],
    database: [
      { name: "MongoDB", icon: SiMongodb, color: "#2F855A" },
      { name: "MySQL", icon: FaDatabase, color: "#DD6B20" },
      { name: "PostgreSQL", icon: FaDatabase, color: "#3182CE" }
    ],
    javascript: [
      { name: "Angular", icon: FaAngular, color: "#E53E3E" },
      { name: "Node.js", icon: FaNodeJs, color: "#2F855A" },
      { name: "React.js", icon: SiReactIcon, color: "#00B5D8" },
      { name: "Vue.js", icon: FaVuejs, color: "#38A169" }
    ],
    "full-stack": [
      { name: "MEAN Stack", icon: SiMongodb, color: "#276749" },
      { name: "MERN Stack", icon: SiExpress, color: "#1A202C" },
      { name: "Blockchain", icon: SiBitcoin, color: "#6B46C1" },
      { name: "DevOps", icon: DiAws, color: "#DD6B20" }
    ],
    "ai-ml": [
      { name: "AI", icon: FaBrain, color: "#805AD5" },
      { name: "Machine Learning", icon: FaLaptopCode, color: "#3182CE" },
      { name: "ChatBot", icon: FaRobot, color: "#319795" },
      { name: "IOT", icon: FaInternetExplorer, color: "#ED8936" },
      { name: "Deep Learning", icon: FaLaptopCode, color: "#C53030" }
    ]
  };

  // 1. Add technology descriptions data
  const techDescriptions = {
    ".Net": {
      emoji: "🖥️",
      what: "A Microsoft framework for building web, desktop, and mobile apps.",
      why: "Robust, scalable, and cross-platform.",
      benefits: "⚡ High performance, 🔒 secure, 🏢 enterprise-ready."
    },
    Python: {
      emoji: "🐍",
      what: "A popular, high-level programming language known for its readability.",
      why: "Great for web, data science, automation, and scripting.",
      benefits: "🚀 Easy to learn, 📚 vast libraries, 🌍 strong community."
    },
    "Ruby On Rails": {
      emoji: "💎",
      what: "A web application framework written in Ruby.",
      why: "Speeds up development with convention over configuration.",
      benefits: "⚡ Rapid prototyping, 🧹 clean code, 🤝 strong community."
    },
    GraphQL: {
      emoji: "🔗",
      what: "A query language for APIs.",
      why: "Fetch exactly the data you need, nothing more.",
      benefits: "🎯 Efficient, 🔄 flexible, 📉 reduces over-fetching."
    },
    PHP: {
      emoji: "🐘",
      what: "A server-side scripting language for web development.",
      why: "Easy to deploy, powers many websites.",
      benefits: "🧩 Simple, 🌐 large ecosystem, 💡 widely supported."
    },
    Laravel: {
      emoji: "🚀",
      what: "Modern PHP framework for web apps.",
      why: "Elegant syntax, secure, built-in tools.",
      benefits: "✨ Elegant syntax, 🛡️ secure, 🧰 built-in tools."
    },
    CodeIgniter: {
      emoji: "⚡",
      what: "Lightweight PHP framework.",
      why: "Simple, fast, easy to learn.",
      benefits: "🏃‍♂️ Fast, 🧑‍💻 easy to learn."
    },
    Zend: {
      emoji: "🏢",
      what: "Enterprise-grade PHP framework.",
      why: "Scalable, secure, professional support.",
      benefits: "🏗️ Scalable, 🔒 secure, 👨‍💼 professional support."
    },
    "React Native": {
      emoji: "⚛️",
      what: "Build native mobile apps using JavaScript and React.",
      why: "Cross-platform, fast development.",
      benefits: "📲 Cross-platform, ⚡ fast development."
    },
    Flutter: {
      emoji: "🦋",
      what: "Google’s UI toolkit for beautiful apps.",
      why: "Gorgeous UI, single codebase.",
      benefits: "🎨 Gorgeous UI, 📝 single codebase."
    },
    iOS: {
      emoji: "🍏",
      what: "Apple’s mobile operating system.",
      why: "High performance, secure, premium user base.",
      benefits: "🚀 High performance, 🔒 secure, 👑 premium user base."
    },
    Android: {
      emoji: "🤖",
      what: "World’s most popular mobile OS.",
      why: "Open source, customizable.",
      benefits: "🌍 Open source, 🛠️ customizable."
    },
    MongoDB: {
      emoji: "🍃",
      what: "NoSQL, document-oriented database.",
      why: "Flexible, scalable, handles big data.",
      benefits: "🧩 Flexible, 📈 scalable."
    },
    MySQL: {
      emoji: "🐬",
      what: "Popular open-source relational database.",
      why: "Reliable, easy to use, widely supported.",
      benefits: "🏆 Reliable, 🔗 widely supported."
    },
    PostgreSQL: {
      emoji: "🐘",
      what: "Advanced open-source relational database.",
      why: "Powerful, extensible, supports complex queries.",
      benefits: "💪 Powerful, 🔌 extensible."
    },
    Angular: {
      emoji: "🅰️",
      what: "TypeScript-based web framework.",
      why: "Robust, scalable, maintained by Google.",
      benefits: "🏗️ Robust, 🚀 scalable."
    },
    "Node.js": {
      emoji: "🟢",
      what: "JavaScript runtime for servers.",
      why: "Fast, scalable, event-driven.",
      benefits: "⚡ Fast, 🔄 event-driven."
    },
    "React.js": {
      emoji: "⚛️",
      what: "Library for building UIs.",
      why: "Component-based, fast, maintained by Facebook.",
      benefits: "🧩 Component-based, ⚡ fast."
    },
    "Vue.js": {
      emoji: "🟩",
      what: "Progressive JavaScript framework.",
      why: "Simple, flexible, easy to integrate.",
      benefits: "🧑‍💻 Simple, 🧰 flexible."
    },
    "MEAN Stack": {
      emoji: "🟩",
      what: "MongoDB, Express.js, Angular, Node.js.",
      why: "Full JavaScript stack, scalable, fast.",
      benefits: "🟢 Full JavaScript stack, 🚀 scalable."
    },
    "MERN Stack": {
      emoji: "🟦",
      what: "MongoDB, Express.js, React.js, Node.js.",
      why: "Modern, flexible, popular for web apps.",
      benefits: "🟦 Modern, 🧩 flexible."
    },
    Blockchain: {
      emoji: "⛓️",
      what: "Distributed ledger technology.",
      why: "Decentralized, transparent, secure.",
      benefits: "🔒 Secure, 🌍 decentralized."
    },
    DevOps: {
      emoji: "⚙️",
      what: "Automates software development and IT operations.",
      why: "Faster delivery, reliability, scalability.",
      benefits: "🚀 Faster delivery, 📈 reliability."
    },
    AI: {
      emoji: "🧠",
      what: "Simulates human intelligence in machines.",
      why: "Automation, smart decision-making.",
      benefits: "🤖 Automation, 🧠 smart decisions."
    },
    "Machine Learning": {
      emoji: "📊",
      what: "Enables systems to learn from data.",
      why: "Predictive analytics, personalization.",
      benefits: "🔮 Predictive analytics, 🎯 personalization."
    },
    ChatBot: {
      emoji: "💬",
      what: "AI-powered conversational agents.",
      why: "24/7 support, automates customer service.",
      benefits: "🕒 24/7 support, 🤖 automates service."
    },
    IOT: {
      emoji: "🌐",
      what: "Connects devices for data exchange.",
      why: "Automation, real-time monitoring.",
      benefits: "📡 Automation, 📊 real-time monitoring."
    },
    "Deep Learning": {
      emoji: "🧬",
      what: "Advanced ML using neural networks.",
      why: "High accuracy in image, speech, and text recognition.",
      benefits: "🖼️ High accuracy in image, speech, and text recognition."
    },
  };

  const getTechnologySection = (categoryTitle, techList) => (
    <Box mb={10}>
      <Heading as="h3" size="lg" mb={6} textDecoration="none">
        {categoryTitle}
      </Heading>
      <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={16}>
        {techList.map((tech, techIndex) => (
          <MotionBox
            key={techIndex}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: techIndex * 0.1 }}
            viewport={{ once: true }}
          >
            <TechnologyCard
              icon={tech.icon}
              name={tech.name}
              color={tech.color}
              onClick={() => handleTechnologyClick(tech.name, setNavbarTop, setNavbarTopTwo, setSelectedTechnology)}
            />
          </MotionBox>
        ))}
      </SimpleGrid>
    </Box>
  );

  return (
    <Box bg={backgroundColor}>
      <Helmet>
        <title>Tech Solutions - Professional Technology Services</title>
        <meta name="description" content="We offer expert technology services including Website Development, App Development, API Development, Chatbot Development, and WhatsApp Bot Development. Discover our services and learn how we can assist your business." />
        <meta name="keywords" content="tech services, website development, app development, API development, chatbot development, WhatsApp bot development, professional tech services" />
        <meta property="og:title" content="Tech Solutions - Professional Technology Services" />
        <meta property="og:description" content="We provide a range of technology services including Website Development, App Development, and more. Learn about our offerings and how we can help your business grow." />
        <meta property="og:url" content="https://www.yourtechwebsite.com" />
        <meta property="og:image" content="https://www.yourtechwebsite.com" />
        <link rel="canonical" href="https://www.yourtechwebsite.com" />
      </Helmet>

      <NavbarTopFirst />
      <NavbarTopSecond />

      <Box pt="130px" minH="auto" position="relative">
        {showAppointmentForm ? (
          <Box minH="auto">
            <Container maxW="container.xl" py={10} px={{ base: 4, md: 6 }}>
              <Button onClick={handleBackToHome} mb={6} bg={primaryColor} color="white" _hover={{ bg: secondaryColor }}>Back to Home</Button>
              <Appointment />
            </Container>
          </Box>
        ) : showContactForm ? (
          <Box minH="auto">
            <Container maxW="container.xl" py={10} px={{ base: 4, md: 6 }}>
              <Button onClick={handleBackToHome} mb={6} bg={primaryColor} color="white" _hover={{ bg: secondaryColor }}>Back to Home</Button>
              <Contact isEmbeddedAsForm={true} />
            </Container>
          </Box>
        ) : showAllTechnologies ? (
          <Box minH="auto">
            <Container maxW="container.xl" py={10} px={{ base: 4, md: 6 }}>
              <Button onClick={handleBackToHome} mb={6} bg={primaryColor} color="white" _hover={{ bg: secondaryColor }}>Back to Home</Button>
              <Box 
                bg="white" 
                borderRadius="2xl" 
                boxShadow="2xl" 
                p={{ base: 6, md: 8 }}
                border="1px solid"
                borderColor="gray.100"
              >
                <VStack spacing={8} align="start">
                  <Heading 
                    as="h2" 
                    size="2xl" 
                    bgGradient={`linear(to-r, ${primaryColor}, ${secondaryColor})`} 
                    bgClip="text"
                    fontWeight="extrabold"
                    textAlign="center"
                    w="100%"
                  >
                    🚀 All Technologies We Work With
                  </Heading>
                  <Text fontSize="lg" color="gray.600" textAlign="center" mb={6}>
                    Explore our comprehensive technology stack organized by categories.
                  </Text>
                  
                  {/* Web Technologies */}
                  <Box w="100%">
                    <Heading as="h3" size="lg" color={primaryColor} mb={4} borderBottom="2px solid" borderColor="blue.200" pb={2}>
                      🌐 Web Technologies
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
                      {Object.entries(technologyDetails)
                        .filter(([name]) => ['.Net', 'Python', 'Ruby On Rails', 'GraphQL', 'PHP'].includes(name))
                        .map(([name, tech]) => (
                          <Box 
                            key={name}
                            p={{ base: 4, md: 6 }} 
                            borderRadius="xl" 
                            bg="blue.50" 
                            border="1px solid"
                            borderColor="blue.200"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl", bg: "blue.100" }}
                            transition="all 0.3s"
                          >
                            <VStack spacing={4} align="start">
                              <HStack spacing={3}>
                                <Text fontSize="3xl">{tech.emoji}</Text>
                                <Heading as="h4" size={{ base: "sm", md: "md" }} color="blue.700" fontWeight="bold">
                                  {tech.title}
                                </Heading>
                              </HStack>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>What is it?</strong> {tech.what}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Why use it?</strong> {tech.why}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Benefits:</strong> {tech.benefits}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                    </SimpleGrid>
                  </Box>

                  {/* PHP Technologies */}
                  <Box w="100%">
                    <Heading as="h3" size="lg" color={primaryColor} mb={4} borderBottom="2px solid" borderColor="orange.200" pb={2}>
                      🐘 PHP Technologies
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
                      {Object.entries(technologyDetails)
                        .filter(([name]) => ['Laravel', 'CodeIgniter', 'Zend'].includes(name))
                        .map(([name, tech]) => (
                          <Box 
                            key={name}
                            p={{ base: 4, md: 6 }} 
                            borderRadius="xl" 
                            bg="orange.50" 
                            border="1px solid"
                            borderColor="orange.200"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl", bg: "orange.100" }}
                            transition="all 0.3s"
                          >
                            <VStack spacing={4} align="start">
                              <HStack spacing={3}>
                                <Text fontSize="3xl">{tech.emoji}</Text>
                                <Heading as="h4" size={{ base: "sm", md: "md" }} color="orange.700" fontWeight="bold">
                                  {tech.title}
                                </Heading>
                              </HStack>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>What is it?</strong> {tech.what}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Why use it?</strong> {tech.why}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Benefits:</strong> {tech.benefits}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                    </SimpleGrid>
                  </Box>

                  {/* Mobile Technologies */}
                  <Box w="100%">
                    <Heading as="h3" size="lg" color={primaryColor} mb={4} borderBottom="2px solid" borderColor="green.200" pb={2}>
                      📱 Mobile Technologies
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
                      {Object.entries(technologyDetails)
                        .filter(([name]) => ['React Native', 'Flutter', 'iOS', 'Android'].includes(name))
                        .map(([name, tech]) => (
                          <Box 
                            key={name}
                            p={{ base: 4, md: 6 }} 
                            borderRadius="xl" 
                            bg="green.50" 
                            border="1px solid"
                            borderColor="green.200"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl", bg: "green.100" }}
                            transition="all 0.3s"
                          >
                            <VStack spacing={4} align="start">
                              <HStack spacing={3}>
                                <Text fontSize="3xl">{tech.emoji}</Text>
                                <Heading as="h4" size={{ base: "sm", md: "md" }} color="green.700" fontWeight="bold">
                                  {tech.title}
                                </Heading>
                              </HStack>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>What is it?</strong> {tech.what}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Why use it?</strong> {tech.why}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Benefits:</strong> {tech.benefits}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                    </SimpleGrid>
                  </Box>

                  {/* Database Technologies */}
                  <Box w="100%">
                    <Heading as="h3" size="lg" color={primaryColor} mb={4} borderBottom="2px solid" borderColor="teal.200" pb={2}>
                      🗄️ Database Technologies
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
                      {Object.entries(technologyDetails)
                        .filter(([name]) => ['MongoDB', 'MySQL', 'PostgreSQL'].includes(name))
                        .map(([name, tech]) => (
                          <Box 
                            key={name}
                            p={{ base: 4, md: 6 }} 
                            borderRadius="xl" 
                            bg="teal.50" 
                            border="1px solid"
                            borderColor="teal.200"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl", bg: "teal.100" }}
                            transition="all 0.3s"
                          >
                            <VStack spacing={4} align="start">
                              <HStack spacing={3}>
                                <Text fontSize="3xl">{tech.emoji}</Text>
                                <Heading as="h4" size={{ base: "sm", md: "md" }} color="teal.700" fontWeight="bold">
                                  {tech.title}
                                </Heading>
                              </HStack>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>What is it?</strong> {tech.what}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Why use it?</strong> {tech.why}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Benefits:</strong> {tech.benefits}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                    </SimpleGrid>
                  </Box>

                  {/* JavaScript Technologies */}
                  <Box w="100%">
                    <Heading as="h3" size="lg" color={primaryColor} mb={4} borderBottom="2px solid" borderColor="yellow.200" pb={2}>
                      ⚡ JavaScript Technologies
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
                      {Object.entries(technologyDetails)
                        .filter(([name]) => ['Angular', 'Node.js', 'React.js', 'Vue.js'].includes(name))
                        .map(([name, tech]) => (
                          <Box 
                            key={name}
                            p={{ base: 4, md: 6 }} 
                            borderRadius="xl" 
                            bg="yellow.50" 
                            border="1px solid"
                            borderColor="yellow.200"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl", bg: "yellow.100" }}
                            transition="all 0.3s"
                          >
                            <VStack spacing={4} align="start">
                              <HStack spacing={3}>
                                <Text fontSize="3xl">{tech.emoji}</Text>
                                <Heading as="h4" size={{ base: "sm", md: "md" }} color="yellow.700" fontWeight="bold">
                                  {tech.title}
                                </Heading>
                              </HStack>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>What is it?</strong> {tech.what}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Why use it?</strong> {tech.why}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Benefits:</strong> {tech.benefits}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                    </SimpleGrid>
                  </Box>

                  {/* Full-Stack Technologies */}
                  <Box w="100%">
                    <Heading as="h3" size="lg" color={primaryColor} mb={4} borderBottom="2px solid" borderColor="purple.200" pb={2}>
                      🏗️ Full-Stack Technologies
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
                      {Object.entries(technologyDetails)
                        .filter(([name]) => ['MEAN Stack', 'MERN Stack', 'Blockchain', 'DevOps'].includes(name))
                        .map(([name, tech]) => (
                          <Box 
                            key={name}
                            p={{ base: 4, md: 6 }} 
                            borderRadius="xl" 
                            bg="purple.50" 
                            border="1px solid"
                            borderColor="purple.200"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl", bg: "purple.100" }}
                            transition="all 0.3s"
                          >
                            <VStack spacing={4} align="start">
                              <HStack spacing={3}>
                                <Text fontSize="3xl">{tech.emoji}</Text>
                                <Heading as="h4" size={{ base: "sm", md: "md" }} color="purple.700" fontWeight="bold">
                                  {tech.title}
                                </Heading>
                              </HStack>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>What is it?</strong> {tech.what}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Why use it?</strong> {tech.why}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Benefits:</strong> {tech.benefits}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                    </SimpleGrid>
                  </Box>

                  {/* AI & ML Technologies */}
                  <Box w="100%">
                    <Heading as="h3" size="lg" color={primaryColor} mb={4} borderBottom="2px solid" borderColor="pink.200" pb={2}>
                      🤖 AI & ML Technologies
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
                      {Object.entries(technologyDetails)
                        .filter(([name]) => ['AI', 'Machine Learning', 'ChatBot', 'IOT', 'Deep Learning'].includes(name))
                        .map(([name, tech]) => (
                          <Box 
                            key={name}
                            p={{ base: 4, md: 6 }} 
                            borderRadius="xl" 
                            bg="pink.50" 
                            border="1px solid"
                            borderColor="pink.200"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl", bg: "pink.100" }}
                            transition="all 0.3s"
                          >
                            <VStack spacing={4} align="start">
                              <HStack spacing={3}>
                                <Text fontSize="3xl">{tech.emoji}</Text>
                                <Heading as="h4" size={{ base: "sm", md: "md" }} color="pink.700" fontWeight="bold">
                                  {tech.title}
                                </Heading>
                              </HStack>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>What is it?</strong> {tech.what}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Why use it?</strong> {tech.why}
                              </Text>
                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700" lineHeight="1.6">
                                <strong>Benefits:</strong> {tech.benefits}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                    </SimpleGrid>
                  </Box>
                </VStack>
              </Box>
            </Container>
          </Box>
        ) : selectedTechnology ? (
          <Box>
            <Container maxW="container.xl" py={10} px={{ base: 4, md: 6 }}>
              <Button 
                onClick={handleBackToHome} 
                mb={6} 
                bg={primaryColor} 
                color="white" 
                _hover={{ bg: secondaryColor }}
                size="lg"
                borderRadius="full"
                px={8}
                fontWeight="bold"
                boxShadow="lg"
              >
                ← Back to Home
              </Button>
              
              <Box 
                bg="white" 
                borderRadius="2xl" 
                boxShadow="2xl" 
                p={{ base: 6, md: 8 }}
                border="1px solid"
                borderColor="gray.100"
                position="relative"
                overflow="hidden"
                _before={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  bgGradient: `linear(to-r, ${primaryColor}, ${secondaryColor})`,
                }}
              >
                <VStack spacing={6} align="start">
                  <HStack spacing={6} align="center" w="100%" flexWrap={{ base: "wrap", md: "nowrap" }}>
                    <Box
                      p={4}
                      borderRadius="full"
                      bgGradient={`linear(to-r, ${primaryColor}, ${secondaryColor})`}
                      boxShadow="xl"
                      flexShrink={0}
                    >
                      <Text fontSize="4xl">{technologyDetails[selectedTechnology]?.emoji}</Text>
                    </Box>
                    <VStack align="start" spacing={2} minW={0} flex={1}>
                      <Heading 
                        as="h2" 
                        size={{ base: "xl", md: "2xl" }}
                        bgGradient={`linear(to-r, ${primaryColor}, ${secondaryColor})`} 
                        bgClip="text"
                        fontWeight="extrabold"
                        letterSpacing="tight"
                        wordBreak="break-word"
                      >
                        {technologyDetails[selectedTechnology]?.title}
                      </Heading>
                      <Text fontSize="lg" color="gray.600" fontWeight="medium">
                        Technology Details
                      </Text>
                    </VStack>
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="100%">
                    <Box 
                      p={6} 
                      borderRadius="xl" 
                      bg="blue.50" 
                      border="1px solid"
                      borderColor="blue.200"
                      _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
                      transition="all 0.3s"
                    >
                      <VStack spacing={4} align="start">
                        <HStack spacing={3}>
                          <Box p={2} borderRadius="full" bg="blue.500" color="white">
                            <Text fontSize="lg">❓</Text>
                          </Box>
                          <Heading as="h3" size="md" color="blue.700" fontWeight="bold">
                            What is it?
                          </Heading>
                        </HStack>
                        <Text fontSize="lg" color="gray.700" lineHeight="1.8" fontWeight="medium">
                          {technologyDetails[selectedTechnology]?.what}
                        </Text>
                      </VStack>
                    </Box>

                    <Box 
                      p={6} 
                      borderRadius="xl" 
                      bg="green.50" 
                      border="1px solid"
                      borderColor="green.200"
                      _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
                      transition="all 0.3s"
                    >
                      <VStack spacing={4} align="start">
                        <HStack spacing={3}>
                          <Box p={2} borderRadius="full" bg="green.500" color="white">
                            <Text fontSize="lg">🎯</Text>
                          </Box>
                          <Heading as="h3" size="md" color="green.700" fontWeight="bold">
                            Why use it?
                          </Heading>
                        </HStack>
                        <Text fontSize="lg" color="gray.700" lineHeight="1.8" fontWeight="medium">
                          {technologyDetails[selectedTechnology]?.why}
                        </Text>
                      </VStack>
                    </Box>

                    <Box 
                      p={6} 
                      borderRadius="xl" 
                      bg="purple.50" 
                      border="1px solid"
                      borderColor="purple.200"
                      _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
                      transition="all 0.3s"
                    >
                      <VStack spacing={4} align="start">
                        <HStack spacing={3}>
                          <Box p={2} borderRadius="full" bg="purple.500" color="white">
                            <Text fontSize="lg">✨</Text>
                          </Box>
                          <Heading as="h3" size="md" color="purple.700" fontWeight="bold">
                            Benefits
                          </Heading>
                        </HStack>
                        <Text fontSize="lg" color="gray.700" lineHeight="1.8" fontWeight="medium">
                          {technologyDetails[selectedTechnology]?.benefits}
                        </Text>
                      </VStack>
                    </Box>
                  </SimpleGrid>

                  <Box 
                    mt={8} 
                    p={6} 
                    borderRadius="xl" 
                    bg="gray.50" 
                    border="1px solid"
                    borderColor="gray.200"
                    w="100%"
                  >
                    <VStack spacing={4} align="start">
                      <Heading as="h4" size="lg" color={primaryColor} fontWeight="bold">
                        🚀 Ready to get started?
                      </Heading>
                      <Text fontSize="md" color="gray.600" lineHeight="1.6">
                        Our expert team can help you implement {technologyDetails[selectedTechnology]?.title} in your next project. 
                        Contact us for a free consultation!
                      </Text>
                      <HStack spacing={4} pt={2} flexWrap="wrap" gap={2}>
                        <Button 
                          onClick={handleGetStartedClick}
                          bg={primaryColor} 
                          color="white" 
                          _hover={{ bg: secondaryColor }}
                          size="lg"
                          borderRadius="full"
                          px={8}
                          fontWeight="bold"
                          minW={{ base: "140px", md: "auto" }}
                        >
                          Get Started
                        </Button>
                        <Button 
                          onClick={handleLearnMoreClick}
                          variant="outline" 
                          color={primaryColor} 
                          borderColor={primaryColor}
                          _hover={{ bg: `${primaryColor}10` }}
                          size="lg"
                          borderRadius="full"
                          px={8}
                          fontWeight="bold"
                          minW={{ base: "140px", md: "auto" }}
                        >
                          Learn More
                        </Button>
                      </HStack>
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            </Container>
          </Box>
        ) : (
          <>
            <HeaderFirst onBookAppointmentClick={handleBookAppointmentClick} />
            <CardFirst />

            {/* Technology Stack Section */}
            <Box 
              position="relative"
              overflow="hidden"
              bg="gray.50"
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: "url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=fit&w=2070&q=80')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 0.1,
                zIndex: 0
              }}
            >
              <Box 
                position="relative"
                zIndex={1}
                py={6}
                bg="rgba(255, 255, 255, 0.9)"
              >
                <Container maxW="container.xl">
                  <VStack spacing={10}>
                    <VStack spacing={12} textAlign="center">
                      <Heading 
                        as="h2" 
                        size="2xl" 
                        bgGradient={`linear(to-r, ${primaryColor}, ${secondaryColor})`} 
                        bgClip="text"
                        letterSpacing="tight"
                        fontWeight="extrabold"
                        textShadow="2px 2px 4px rgba(0,0,0,0.4)"
                        _hover={{
                          transform: "scale(1.02)",
                          transition: "all 0.3s ease"
                        }}
                      >
                        Our Technology Stack
                      </Heading>
                      <Text 
                        fontSize="xl" 
                        color="gray.600" 
                        maxW="800px"
                        mb={6}
                        fontFamily="heading"
                        lineHeight="1.8"
                        letterSpacing="wide"
                        fontWeight="medium"
                        sx={{
                          textShadow: "1px 1px 2px rgba(0,0,0,0.05)",
                          '&::first-letter': {
                            fontSize: '1.5em',
                            fontWeight: 'bold',
                            color: primaryColor
                          }
                        }}
                      >
                        We work with cutting-edge technologies to deliver the best solutions for your business.
                      </Text>
                    </VStack>

                    {Object.entries(technologies).map(([category, techs], index) => (
                      <MotionBox 
                        key={category} 
                        w="100%"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Flex direction={{ base: "column", md: "row" }} alignItems={{ base: "center", md: "flex-start" }} mb={2}>
                          <Box minW={{ md: "240px" }} textAlign={{ base: "center", md: "left" }} mb={{ base: 1, md: 0 }} mr={{ base: 0, md: 6 }} pt={1}>
                            <Heading 
                              as="h3" 
                              size="md" 
                              fontWeight="semibold" 
                              textTransform="capitalize" 
                              color={textColor}
                              _hover={{
                                color: primaryColor,
                                transition: "all 0.3s ease"
                              }}
                            >
                              {category} Technologies
                            </Heading>
                          </Box>
                          <Flex wrap="wrap" gap={{ base: 2, md: 4 }} justify={{ base: "center", md: "flex-start" }}>
                            {techs.map((tech, techIndex) => (
                              <MotionBox
                                key={techIndex}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: techIndex * 0.1 }}
                                viewport={{ once: true }}
                              >
                                <TechnologyCard
                                  icon={tech.icon}
                                  name={tech.name}
                                  color={tech.color}
                                  onClick={() => handleTechnologyClick(tech.name, setNavbarTop, setNavbarTopTwo, setSelectedTechnology)}
                                />
                              </MotionBox>
                            ))}
                          </Flex>
                        </Flex>
                        {/* Show selected technology description and all others below */}
                        {selectedTechnology && (
                          <Box mt={8} mb={4} p={6} borderRadius="lg" bg="white" boxShadow="md">
                            <HStack spacing={4} mb={2}>
                              <Text fontSize="2xl">{techDescriptions[selectedTechnology]?.emoji}</Text>
                              <Heading as="h4" size="md">{selectedTechnology}</Heading>
                            </HStack>
                            <Text><b>What is it?</b> {techDescriptions[selectedTechnology]?.what}</Text>
                            <Text><b>Why use it?</b> {techDescriptions[selectedTechnology]?.why}</Text>
                            <Text><b>Benefits:</b> {techDescriptions[selectedTechnology]?.benefits}</Text>
                          </Box>
                        )}
                        {selectedTechnology && (
                          <Box>
                            <Heading as="h5" size="sm" mb={2} color={primaryColor}>Explore More Technologies</Heading>
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                              {Object.entries(techDescriptions)
                                .filter(([name]) => name !== selectedTechnology)
                                .map(([name, desc]) => (
                                  <Box key={name} p={4} borderRadius="md" bg="gray.50" boxShadow="sm" _hover={{ boxShadow: "md", bg: "gray.100" }}>
                                    <HStack spacing={3} mb={1}>
                                      <Text fontSize="xl">{desc.emoji}</Text>
                                      <Text fontWeight="bold">{name}</Text>
                                    </HStack>
                                    <Text fontSize="sm"><b>What is it?</b> {desc.what}</Text>
                                    <Text fontSize="sm"><b>Why use it?</b> {desc.why}</Text>
                                    <Text fontSize="sm"><b>Benefits:</b> {desc.benefits}</Text>
                                  </Box>
                                ))}
                            </SimpleGrid>
                          </Box>
                        )}
                        <Box w="100%" borderBottom="1px solid" borderColor="gray.200" mb={2} />
                      </MotionBox>
                    ))}
                  </VStack>
                </Container>
              </Box>
            </Box>

            {/* Features Section */}
            <Box py={20} bg={backgroundColor}>
              <Container maxW="container.xl">
                <VStack spacing={12}>
                  <VStack spacing={4} textAlign="center">
                    <Heading as="h2" size="2xl" bgGradient={`linear(to-r, ${primaryColor}, ${secondaryColor})`} bgClip="text">
                      Why Choose Us?
                    </Heading>
                    <Text fontSize="xl" color="gray.600" maxW="800px">
                      We combine technical expertise with business understanding to deliver exceptional results.
                    </Text>
                  </VStack>

                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10} w="100%">
                    {features.map((feature, index) => (
                      <MotionBox
                        key={index}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        bg={cardBackground}
                        p={6}
                        borderRadius="lg"
                        boxShadow="md"
                        _hover={{ boxShadow: "xl", transform: "translateY(-5px)" }}
                      >
                        <VStack spacing={4} align="center">
                          <Icon as={feature.icon} w={12} h={12} color={feature.color} />
                          <Heading as="h3" size="md" textAlign="center" color={textColor}>
                            {feature.title}
                          </Heading>
                          <Text fontSize="sm" textAlign="center" color={textColor}>
                            {feature.description}
                          </Text>
                        </VStack>
                      </MotionBox>
                    ))}
                  </SimpleGrid>
                </VStack>
              </Container>
            </Box>

            {/* Testimonials Section */}
            {/* <Box py={20}>
              <Container maxW="container.xl">
                <VStack spacing={12}>
                  <VStack spacing={4} textAlign="center">
                    <Heading as="h2" size="2xl" bgGradient={`linear(to-r, ${primaryColor}, ${secondaryColor})`} bgClip="text">
                      What Our Clients Say
                    </Heading>
                    <Text fontSize="xl" color="gray.600" maxW="800px">
                      Don't just take our word for it. Here's what our clients have to say about working with us.
                    </Text>
                  </VStack>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                    {testimonials.map((testimonial, index) => (
                      <TestimonialCard key={index} {...testimonial} />
                    ))}
                  </SimpleGrid>
                </VStack>
              </Container>
            </Box> */}

            {/* Call to Action Section */}
            <Box py={20} bg={backgroundColor}>
              <Container maxW="container.xl" textAlign="center">
                <VStack spacing={8} textAlign="center">
                  <Heading as="h2" size="2xl" bgGradient={`linear(to-r, ${primaryColor}, ${secondaryColor})`} bgClip="text">
                    Ready to Start Your Project?
                  </Heading>
                  <Text fontSize="xl" color="gray.600" maxW="800px">
                    Let's work together to bring your ideas to life. Contact us today for a free consultation.
                  </Text>
                  <HStack spacing={4}>
                    <Link to="/contact">
                      <Button
                        size="lg"
                        bg={primaryColor}
                        color="white"
                        _hover={{ bg: secondaryColor }}
                        px={8}
                      >
                        Get Started
                      </Button>
                    </Link>
                    <Link to="/services">
                      <Button
                        size="lg"
                        variant="outline"
                        color={primaryColor}
                        borderColor={primaryColor}
                        _hover={{ bg: buttonHoverColor }}
                        px={8}
                      >
                        Learn More
                      </Button>
                    </Link>
                  </HStack>
                </VStack>
              </Container>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default Home;
